import { memo } from "react";

import { Icon } from "@components/Icon";
import { Video, type VideoProps } from "@components/Video";
import { type ModalContainerProps } from "@sections/ModalProvider";
import { type TTailwindString, tw } from "classnames";

type Props = VideoProps & {
  ariaDescribedby?: string;
  ariaLabel?: string;
  closeAriaLabel?: string;
  modalAria?: {
    "aria-describedby"?: string;
    "aria-label"?: string;
    "aria-labelledby"?: string;
    "aria-modal": boolean;
    role: "dialog" | "alertdialog";
  };
  modalProps: ModalContainerProps;
  onClose: (
    e: React.MouseEvent<HTMLElement, MouseEvent> | KeyboardEvent
  ) => void;
};

const styles = {
  modal: tw(
    "box-border",
    "md:max-w-[calc(100vw-160px)]" as TTailwindString,
    "h-auto"
  ),
  closeButton: tw(
    "fixed",
    "right-4",
    "top-4",
    "flex",
    "h-10",
    "w-10",
    "cursor-pointer",
    "items-center",
    "justify-center",
    "overflow-hidden",
    "rounded-full",
    "border",
    "border-solid",
    "border-white",
    "bg-black",
    "text-white",
    "md:right-8",
    "md:top-8",
    "md:h-16",
    "md:w-16"
  ),
  closeIconContainer: tw(
    "flex",
    "scale-[0.6]" as TTailwindString,
    "md:scale-100"
  ),
};

const VideoModalViewBase = ({
  ariaDescribedby,
  ariaLabel,
  closeAriaLabel,
  modalAria,
  modalProps,
  onClose,
  ...videoProps
}: Props) => {
  return (
    <div
      aria-describedby={ariaDescribedby}
      aria-label={ariaLabel}
      className={styles.modal}
      {...modalProps}
      {...(modalAria || {})}
    >
      <Video {...videoProps} />
      <button
        aria-label={closeAriaLabel}
        className={styles.closeButton}
        onClick={onClose}
      >
        <div className={styles.closeIconContainer}>
          <Icon name="close" />
        </div>
      </button>
    </div>
  );
};

export const VideoModalView = memo(VideoModalViewBase);
